export interface Course {
  /** URL slug → /cursos/:slug */
  slug: string;
  /** Etiqueta corta (norma / categoría) */
  tag: string;
  /** Título principal del curso */
  title: string;
  /** Subtítulo / promesa */
  subtitle: string;
  /** Descripción breve para el hero */
  description: string;
  /** Objetivo del programa */
  objetivo: string;
  /** Temario "Lo que aprenderás" */
  aprenderas: string[];
  /** Precio del diplomado en pesos colombianos (COP). Opcional: si no se define, no se muestra. */
  price?: number;
  /** Etiqueta de la sección de valor (varía por curso) */
  valueLabel: string;
  /** Beneficios / resultados */
  valueTags: string[];
  /** Mensaje pre-rellenado de WhatsApp */
  whatsappMessage: string;
  /** SEO / Open Graph */
  metaTitle: string;
  metaDescription: string;
}

/** Número de WhatsApp de GAELI (formato internacional sin signos). */
export const WHATSAPP_NUMBER = '573203064734';

/** Construye el enlace de WhatsApp con el mensaje pre-rellenado. */
export function whatsappLink(message: string): string {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

/** Formatea un precio en pesos colombianos (p. ej. 110000 → "$110.000"). */
export function formatPrice(value: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(value);
}

export const COURSES: Course[] = [
  {
    slug: 'auditoria-iso-21001',
    tag: 'Diplomado · ISO 21001:2025',
    title: 'Auditoría ISO 21001:2025',
    subtitle: 'Conviértete en auditor interno de organizaciones educativas',
    description:
      'Programa virtual especializado para formar auditores internos bajo la norma ISO 21001:2025, con un enfoque práctico y aplicable desde el primer día.',
    objetivo:
      'Desarrollar las competencias para planificar, ejecutar y liderar auditorías internas que garanticen la calidad en organizaciones educativas.',
    aprenderas: [
      'Interpretación de la norma ISO 21001',
      'Planificación de auditorías',
      'Ejecución y recolección de evidencias',
      'Gestión de hallazgos',
      'Mejora continua',
    ],
    price: 450000,
    valueLabel: 'Valor para ti y tu organización',
    valueTags: [
      'Mejora en procesos educativos',
      'Cumplimiento normativo',
      'Fortalecimiento institucional',
      'Perfil profesional competitivo',
    ],
    whatsappMessage:
      'Hola GAELI 👋, vi la publicidad del diplomado "Auditoría ISO 21001:2025" y quiero más información (fechas, inversión e inscripción).',
    metaTitle: 'Diplomado Auditoría ISO 21001:2025 | GAELI Academy',
    metaDescription:
      'Fórmate como auditor interno en organizaciones educativas bajo ISO 21001:2025. Diplomado 100% virtual y certificado. Pide información por WhatsApp.',
  },
  {
    slug: 'diplomado-iso-21001',
    tag: 'Diplomado · ISO 21001:2025',
    title: 'Diplomado ISO 21001:2025',
    subtitle: 'Implementa sistemas de gestión educativa de alto nivel',
    description:
      'Aprende a estructurar, implementar y optimizar sistemas de gestión para organizaciones educativas bajo estándares internacionales.',
    objetivo:
      'Capacitar en la implementación y mejora del sistema de gestión para organizaciones educativas conforme a ISO 21001:2025.',
    aprenderas: [
      'Contexto organizacional',
      'Liderazgo estratégico',
      'Gestión de procesos',
      'Evaluación del desempeño',
      'Mejora continua',
    ],
    price: 110000,
    valueLabel: 'Resultados esperados',
    valueTags: [
      'Procesos organizados y eficientes',
      'Mayor calidad educativa',
      'Ventaja competitiva institucional',
      'Preparación para certificación',
    ],
    whatsappMessage:
      'Hola GAELI 👋, vi la publicidad del "Diplomado ISO 21001:2025" y quiero más información (fechas, inversión e inscripción).',
    metaTitle: 'Diplomado ISO 21001:2025 Gestión Educativa | GAELI Academy',
    metaDescription:
      'Implementa y optimiza sistemas de gestión educativa bajo ISO 21001:2025. Diplomado 100% virtual y certificado. Pide información por WhatsApp.',
  },
  {
    slug: 'diversidad-e-inclusion',
    tag: 'Diplomado · Diversidad e Inclusión',
    title: 'Gestión Estratégica de la Diversidad e Inclusión',
    subtitle: 'Construye organizaciones inclusivas y sostenibles',
    description:
      'Programa enfocado en estrategias reales de diversidad e inclusión organizacional, con impacto medible en el clima y la reputación de tu empresa.',
    objetivo:
      'Fortalecer la gestión organizacional mediante la implementación de estrategias inclusivas y sostenibles.',
    aprenderas: [
      'Diversidad e inclusión en el entorno organizacional',
      'Marco normativo',
      'Cultura inclusiva',
      'Estrategias de implementación',
      'Medición de impacto',
    ],
    valueLabel: 'Impacto organizacional',
    valueTags: [
      'Mejor clima laboral',
      'Mayor innovación',
      'Cumplimiento legal',
      'Reputación organizacional',
    ],
    whatsappMessage:
      'Hola GAELI 👋, vi la publicidad del diplomado "Gestión Estratégica de la Diversidad e Inclusión" y quiero más información (fechas, inversión e inscripción).',
    metaTitle: 'Diplomado Gestión de la Diversidad e Inclusión | GAELI Academy',
    metaDescription:
      'Aprende a construir organizaciones inclusivas y sostenibles con impacto medible. Diplomado 100% virtual y certificado. Pide información por WhatsApp.',
  },
  {
    slug: 'sgc-iso-9001',
    tag: 'Diplomado · ISO 9001:2015',
    title: 'Implementación Estratégica del Sistema de Gestión de Calidad',
    subtitle: 'Formación práctica para organizaciones más competitivas y sostenibles',
    description:
      'Aprende a diseñar, implementar y mejorar el sistema de gestión de calidad bajo ISO 9001:2015 con un enfoque estratégico y aplicable a cualquier sector.',
    objetivo:
      'Desarrollar las competencias para implementar y mantener un Sistema de Gestión de Calidad eficiente y sostenible conforme a ISO 9001:2015.',
    aprenderas: [
      'Fundamentos del Sistema de Gestión de Calidad',
      'Diseño e implementación del SGC',
      'Control y gestión de la información documentada',
      'Auditoría, evaluación y mejora del SGC',
    ],
    price: 110000,
    valueLabel: 'Valor para ti y tu organización',
    valueTags: [
      'Formación especializada',
      'Certificación de calidad',
      'Enfoque en sostenibilidad',
      'Docentes expertos',
    ],
    whatsappMessage:
      'Hola GAELI 👋, vi la publicidad del diplomado "Implementación Estratégica del SGC ISO 9001:2015" y quiero más información (fechas, inversión e inscripción).',
    metaTitle: 'Diplomado Implementación SGC ISO 9001:2015 | GAELI Academy',
    metaDescription:
      'Diseña, implementa y mejora tu Sistema de Gestión de Calidad ISO 9001:2015. Diplomado 100% virtual y certificado. Pide información por WhatsApp.',
  },
];

/** Devuelve el curso por slug, o undefined si no existe. */
export function findCourse(slug: string): Course | undefined {
  return COURSES.find((c) => c.slug === slug);
}
